/** @file Playback-Controls UI: reset/back/play/forward/slider + keyboard shortcuts. */
import { appStore } from "../core/state.js";
import { h, clear } from "./dom.js";

/** @typedef {ReturnType<typeof import("../playback/engine.js").createPlaybackEngine>} Engine */

/**
 * @param {HTMLElement} container
 * @param {{ engine: Engine, store?: typeof appStore }} deps
 * @returns {() => void}  unmount
 */
export function mountPlaybackControls(container, deps) {
  const { engine, store = appStore } = deps;

  let playPauseBtn = /** @type {HTMLButtonElement | null} */ (null);
  let stepBackBtn = /** @type {HTMLButtonElement | null} */ (null);
  let stepFwdBtn = /** @type {HTMLButtonElement | null} */ (null);
  let slider = /** @type {HTMLInputElement | null} */ (null);

  function render() {
    const state = store.get();
    const { fsmState } = engine.getState();
    clear(container);

    const row = h(
      "div",
      {
        class:
          "pb-controls flex items-center gap-2 p-2 bg-neutral-900 border-t border-neutral-800",
      },
      [],
    );

    const resetBtn = h(
      "button",
      {
        type: "button",
        class: "min-h-[44px] min-w-[44px] bg-neutral-700 hover:bg-neutral-600 text-white rounded px-3",
        "aria-label": "Reset",
      },
      ["⏮"],
    );
    resetBtn.addEventListener("click", () => {
      engine.reset();
    });
    row.appendChild(resetBtn);

    stepBackBtn = /** @type {HTMLButtonElement} */ (
      h(
        "button",
        {
          type: "button",
          class:
            "min-h-[44px] min-w-[44px] bg-neutral-700 hover:bg-neutral-600 text-white rounded px-3",
          "aria-label": "Eine Scene zurück",
          disabled: fsmState === "playing" ? true : null,
        },
        ["⏪"],
      )
    );
    stepBackBtn.addEventListener("click", () => {
      engine.stepBackward();
    });
    row.appendChild(stepBackBtn);

    const playLabel = fsmState === "playing" ? "⏸" : "▶";
    playPauseBtn = /** @type {HTMLButtonElement} */ (
      h(
        "button",
        {
          type: "button",
          class:
            "min-h-[44px] min-w-[44px] bg-emerald-700 hover:bg-emerald-600 text-white rounded px-3",
          "aria-label": fsmState === "playing" ? "Pause" : "Play",
        },
        [playLabel],
      )
    );
    playPauseBtn.addEventListener("click", () => {
      if (engine.getState().fsmState === "playing") {
        engine.pause();
      } else {
        engine.play();
      }
    });
    row.appendChild(playPauseBtn);

    stepFwdBtn = /** @type {HTMLButtonElement} */ (
      h(
        "button",
        {
          type: "button",
          class:
            "min-h-[44px] min-w-[44px] bg-neutral-700 hover:bg-neutral-600 text-white rounded px-3",
          "aria-label": "Eine Scene vor",
          disabled: fsmState === "playing" ? true : null,
        },
        ["⏩"],
      )
    );
    stepFwdBtn.addEventListener("click", () => {
      engine.stepForward();
    });
    row.appendChild(stepFwdBtn);

    slider = /** @type {HTMLInputElement} */ (
      h("input", {
        type: "range",
        class: "pb-slider flex-1 min-h-[44px]",
        min: 0,
        max: Math.max(0, state.scenes.length - 1),
        step: 1,
        value: String(state.activeSceneIndex),
        "aria-label": "Scene-Auswahl",
      })
    );
    slider.addEventListener("input", () => {
      if (engine.getState().fsmState === "playing") {
        engine.pause();
      }
      const idx = Number(slider?.value ?? 0);
      engine.jumpToScene(idx);
    });
    row.appendChild(slider);

    container.appendChild(row);
  }

  render();
  const unsubStore = store.subscribe((next, prev) => {
    if (next.scenes !== prev.scenes || next.activeSceneIndex !== prev.activeSceneIndex) {
      render();
    }
  });
  const unsubFsm = engine.subscribe(() => render());

  /** @param {KeyboardEvent} e */
  function onKeyDown(e) {
    const active = document.activeElement;
    if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) {
      return;
    }
    if (e.code === "Space") {
      e.preventDefault();
      if (engine.getState().fsmState === "playing") {
        engine.pause();
      } else {
        engine.play();
      }
    } else if (e.code === "ArrowLeft") {
      engine.stepBackward();
    } else if (e.code === "ArrowRight") {
      engine.stepForward();
    }
  }
  window.addEventListener("keydown", onKeyDown);

  return () => {
    unsubStore();
    unsubFsm();
    window.removeEventListener("keydown", onKeyDown);
    clear(container);
  };
}
